import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { HomeOutlined, PermIdentityOutlined, SensorsOutlined } from '@mui/icons-material'
import { Button, Grid, Typography, useMediaQuery } from '@mui/material'
import { useSnackbar } from 'notistack'

import BackButton from '../../../components/back-button'
import ScreenContainer from '../../../components/screen'
import activatedSlimSensor from '../../../images/activated-slim.png'
import activatedSlimOfflineSensor from '../../../images/activated-slim-offline.png'
import { useActivationContext } from '../../../providers/activation-provider'
import Api from '../../../utils/api'
import { SHORT_SCREEN_HEIGHT, VERIFY_FOLDER_ID, VERIFY_NODE_ID } from '../../../utils/constants'

import useStyles from './index.styles'

const CompleteSetupSlim = ({ node }: { node: INodeType | undefined }) => {
  const navigate = useNavigate()
  const isShortScreen = useMediaQuery(`(max-height:${SHORT_SCREEN_HEIGHT})`)
  const classes = useStyles
  const { enqueueSnackbar } = useSnackbar()
  const { activationLocationState, setIsFirstSlim } = useActivationContext()
  const { folderId } = activationLocationState
  const [device, setDevice] = useState<IDeviceType | null>(null)

  const fetchData = useCallback(async () => {
    if (!node?.id) return
    setDevice(null)
    const res = await Api.get(`/api/v2/protect/devices/${node.id}?isActivating=false`)
    if (res.ok) {
      setDevice(res.body)
    } else {
      enqueueSnackbar('Failed to fetch device data', { variant: 'error' })
    }
  }, [enqueueSnackbar, node?.id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const postActivationCleanup = () => {
    localStorage.removeItem(VERIFY_FOLDER_ID)
    localStorage.removeItem(VERIFY_NODE_ID)
  }

  return (
    <ScreenContainer
      loading={!node || !device}
      useDarkBackground={true}
      headerProps={{
        showDownloadAppButton: false,
        leftIcon: <BackButton />,
        useDarkBackground: true,
        title: (
          <Typography variant="body2" sx={classes.slimHeaderTitle}>
            Leak and Freeze
          </Typography>
        ),
      }}
    >
      <Grid container={true} sx={classes.slimMainContainer}>
        <Grid size={12} sx={classes.slimLocationSection}>
          <Grid container={true} size={12} sx={classes.slimLocationContainer}>
            <Grid size={12}>
              <Grid container={true} size={12} sx={classes.slimLocationContainer}>
                <Typography variant="h6" sx={classes.slimLocationText}>
                  {node?.building}
                </Typography>
              </Grid>

              <Grid container={true} size={12} sx={classes.slimLocationContainer}>
                <Typography variant="body1" sx={classes.slimLocationText}>
                  {node?.area}
                </Typography>
              </Grid>

              <Grid container={true} size={12} sx={classes.slimLocationContainer}>
                <Typography variant="body2" sx={classes.slimLocationText}>
                  Floor {node?.floor}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container={true} size={12} sx={classes.slimImageContainer}>
          <img src={device?.isOffline ? activatedSlimOfflineSensor : activatedSlimSensor} alt="Activated Slim Sensor" />

          <Grid container={true} size={12} sx={classes.slimStatusContainer}>
            {device?.isOffline ? (
              <>
                <Typography variant="h6" sx={classes.slimOfflineTitle}>
                  Device is Activated!
                </Typography>

                <Typography variant="body1" sx={classes.slimOfflineText}>
                  Good news – your sensor is coming online.
                  <br />
                  While it connects, the device will beep if water is detected. We'll let you know via email as soon as alerts can begin.
                  <br />
                  <br />
                  No further action is needed at this time.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h4" sx={classes.slimOnlineTitle}>
                  Congratulations!
                </Typography>
                <Typography variant="body1" sx={classes.slimOnlineText}>
                  Your device is now activated and connected
                </Typography>
              </>
            )}
          </Grid>
        </Grid>

        <Grid sx={{ marginTop: isShortScreen ? 4 : 9 }}>
          <Grid container={true} size={12} spacing={2} sx={{ justifyContent: 'center' }}>
            <Grid size={6}>
              <Button
                variant="outlined"
                onClick={async () => {
                  postActivationCleanup()
                  setIsFirstSlim(false)
                  navigate('/sensor-activation', { state: { fromCompleteSetup: true } })
                }}
                fullWidth={true}
                startIcon={<SensorsOutlined />}
                sx={classes.slimOutlinedButton}
              >
                Add Sensor
              </Button>
            </Grid>

            <Grid size={6}>
              <Button
                variant="outlined"
                onClick={() => {
                  postActivationCleanup()
                  navigate(`/dashboard/${folderId}/contacts`)
                }}
                fullWidth={true}
                startIcon={<PermIdentityOutlined />}
                sx={classes.slimOutlinedButton}
              >
                Add Contact
              </Button>
            </Grid>
          </Grid>

          <Grid size={12} sx={classes.slimHomeButtonContainer}>
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              onClick={() => {
                postActivationCleanup()
                navigate(`/dashboard/${folderId}`, { state: { showBookmarkInfo: true } })
              }}
              fullWidth={true}
              startIcon={<HomeOutlined />}
            >
              I'm done
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </ScreenContainer>
  )
}

export default CompleteSetupSlim
